import { useRef } from "react";

// "Pull past the top to dismiss" — iOS sheet pattern, distance-
// AND-velocity thresholded, with seamless scroll → pull handoff
// inside a single touch.
//
// On a large card the user typically isn't at scrollTop=0 when
// they start a touch — they scroll up first, hit the top, then
// keep pulling. Native iOS sheets dismiss without the user
// releasing and re-touching; this hook does the same:
//
//   1. Pointerdown arms a touch context. We don't decide yet
//      whether this is a dismiss gesture; we just track the
//      pointer id.
//   2. As the user moves their finger, we read scrollTop on
//      every sample. The MOMENT it lands at <= 0 we record the
//      finger's current Y (and timestamp) as the "overscroll
//      anchor" — the point from which a downward pull counts
//      toward the dismiss threshold.
//   3. While scrollTop is at the top, every subsequent
//      downward movement past DISMISS_DISTANCE_PX from the
//      anchor fires onDismiss. A faster flick (distance >=
//      VELOCITY_GUARD_PX moving at >= DISMISS_VELOCITY_PX_MS)
//      also fires, so users can throw the sheet away without
//      having to drag the full threshold distance — important
//      on tall cards where the finger may already be near the
//      top of the screen by the time scrollTop hits 0 and
//      there's no room left to keep pulling.
//   4. If the user scrolls back into content (scrollTop > 0),
//      the anchor clears so any new arrival at the top starts
//      a fresh measurement.
//
// The grabber pill stays separate (uses framer's dragControls
// directly for its visceral drag-follows-finger feedback). This
// hook is for the "scroll past the top to flick the sheet away"
// gesture, which is intent-clear once distance OR velocity lands.
//
// Skipped: touches that originate on interactive elements
// (inputs, buttons, chips, sliders, listboxes / options /
// contenteditable). Those keep their own gesture semantics so a
// slider drag or input tap can never dismiss a sheet.
const INTERACTIVE_SELECTOR =
  "input, textarea, select, button, label, " +
  "[role='button'], [role='option'], [role='listbox'], " +
  "[role='slider'], [role='spinbutton'], [contenteditable='true']";

// Distance trigger: smaller than before (was 100) so a deliberate
// pull at scrollTop=0 dismisses without needing to travel a full
// thumb's reach — particularly important on tall cards where the
// finger has limited downward room after reaching the top.
const DISMISS_DISTANCE_PX  = 70;
// Velocity trigger: a quick downward flick past VELOCITY_GUARD_PX
// at >= DISMISS_VELOCITY_PX_MS dismisses even if it never reaches
// the distance threshold. Guard prevents a single jittery sample
// from registering as a flick.
const VELOCITY_GUARD_PX    = 24;
const DISMISS_VELOCITY_PX_MS = 0.9;

export function useSheetDismissAtTop(sheetRef, onDismiss) {
  // Per-gesture state. Refs (not state) — pointermove fires every
  // frame, we don't want re-renders.
  const ctxRef = useRef(null);
  // shape: {
  //   pointerId,
  //   anchorY: number | null,  // finger Y when scrollTop most recently hit top
  //   blocked: boolean,        // touch began on an interactive element
  //   lastY: number,           // last sampled finger Y
  //   lastT: number,           // last sample timestamp (performance.now)
  //   vy: number,              // last computed velocity (px/ms, downward positive)
  // }

  const onPointerDown = (e) => {
    const blocked = !!(e.target instanceof Element && e.target.closest(INTERACTIVE_SELECTOR));
    const atTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
    ctxRef.current = {
      pointerId: e.pointerId,
      anchorY: atTop ? e.clientY : null,
      blocked,
      lastY: e.clientY,
      lastT: performance.now(),
      vy: 0,
    };
  };

  const onPointerMove = (e) => {
    const ctx = ctxRef.current;
    if (!ctx || ctx.blocked) return;
    if (e.pointerId !== ctx.pointerId) return;
    const atTop = !sheetRef.current || sheetRef.current.scrollTop <= 0;
    if (!atTop) {
      // User scrolled into content — clear the anchor so we don't
      // fire on stale finger displacement. A subsequent return to
      // the top will re-anchor.
      ctx.anchorY = null;
      return;
    }
    // Just landed at the top this frame — anchor here so the
    // overscroll distance is measured from the moment scroll
    // boundary was reached, not from where the touch started
    // deeper in the content.
    if (ctx.anchorY == null) {
      ctx.anchorY = e.clientY;
      ctx.lastY = e.clientY;
      ctx.lastT = performance.now();
      ctx.vy = 0;
      return;
    }
    const now = performance.now();
    const dt  = now - ctx.lastT;
    if (dt > 0) ctx.vy = (e.clientY - ctx.lastY) / dt;
    ctx.lastY = e.clientY;
    ctx.lastT = now;

    const dy = e.clientY - ctx.anchorY;
    const distanceTrip = dy >= DISMISS_DISTANCE_PX;
    const velocityTrip = dy >= VELOCITY_GUARD_PX && ctx.vy >= DISMISS_VELOCITY_PX_MS;
    if (distanceTrip || velocityTrip) {
      ctxRef.current = null;
      onDismiss && onDismiss();
    }
  };

  const onPointerUp = () => { ctxRef.current = null; };
  const onPointerCancel = onPointerUp;

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
